
// Consume transit cookie set by the auth page on cross-origin redirect
(function () {
    const m = document.cookie.match(/(?:^|; )rd_session=([^;]*)/);
    if (m) {
        try {
            const s = JSON.parse(decodeURIComponent(m[1]));
            sessionStorage.setItem('rd_access_token', s.token);
            sessionStorage.setItem('rd_access_expires', s.expiresAt);
            sessionStorage.setItem('rd_user', JSON.stringify(s.user));
        } catch {}
        document.cookie = 'rd_session=; Max-Age=0; Path=/';
    }
})();

let _projectId   = null;
let _activePageId = null;
let _patchHandler   = null;
let _catchUpHandler = null;
let _catchUpNeededHandler = null;

// After SignalR reconnects the server has a new connectionId — re-join and catch up.
window.onHubReconnected(async function onHubReconnectedHandler() {
    if (!_projectId) return;
    try {
        await window.joinProject(_projectId);
        if (_activePageId) {
            const seq = window['_DomSession']?.tree?.seq ?? 0;
            await window.catchUp(_activePageId, seq);
        }
    } catch (e) {
        console.error('[hub] reconnect re-join failed:', e);
    }
});

// Covers brief network drops where SignalR stayed "Connected" but patches were missed.
window.addEventListener('online', async function onNetworkOnline() {
    if (!_activePageId || window.hubState() !== 'Connected') return;
    try {
        const seq = window['_DomSession']?.tree?.seq ?? 0;
        await window.catchUp(_activePageId, seq);
    } catch (e) {
        console.error('[hub] online catch-up failed:', e);
    }
});

/**
 * Connects the hub, joins the project, loads the snapshot for the given page,
 * and wires all incoming patch events to DomSession.
 * Call once on project open, then call openPage() to switch pages.
 */
window.initHub = async function initHub(projectId, pageId) {
    const user = JSON.parse(sessionStorage.getItem('rd_user') ?? 'null');
    if (!user) { window.location.href = '/F/Auth/index.html'; return; }

    _projectId = projectId;

    if (window.hubState() !== 'Connected')
        await window.hubConnect();

    console.log(await window.joinProject(projectId));
    if (pageId) await window.openPage(projectId, pageId);
};

/**
 * Switches the active page: reinitialises DomSession, loads snapshot, catches up.
 * Hub must already be connected and the project joined via initHub().
 */
window.openPage = async function openPage(projectId, pageId) {
    const user = window.getCurrentUser();
    if (!user) return;

    _activePageId = pageId;

    // Initialise DomSession with the real pageId and userId before CanvasManager touches it.
    window['_DomSession'] = new window.DomSession(pageId, user.id);

    // Ensure the bridge exists — first call creates it from the idle canvas iframe.
    if (window['_IframeBridge']) {
        window['_IframeBridge'].rewire(window['_DomSession']);
    } else {
        const frame = document.getElementById('canvas');
        if (frame && window.IframeBridge) {
            window['_IframeBridge'] = new window.IframeBridge(window['_DomSession'], frame.contentDocument);
        }
    }

    const page = await window.getPage(projectId, pageId);
    if (!page) throw new Error(`Page ${pageId} not found in project ${projectId}`);

    let snapshotData = await window.getPageSnapshot(projectId, pageId);

    // Empty page — create the HTML skeleton via REST then re-fetch.
    if (snapshotData.nodes.length === 0) {
        await window.buildPageSkeleton(projectId, pageId, page.pageType);
        snapshotData = await window.getPageSnapshot(projectId, pageId);
    }

    const snapshot = window.DomSession.snapshotFromServerNodes(page, snapshotData);

    const bridge = window['_IframeBridge'];
    if (bridge) {
        bridge.loadSnapshot(snapshot);
    } else {
        console.warn('[openPage] IframeBridge unavailable — tree loaded but iframe not rendered');
        window['_DomSession'].loadSnapshot(snapshot);
    }

    _wirePatchEvents(projectId);
};

function _wirePatchEvents(projectId) {
    if (_patchHandler)   window.removeEventListener('hub.receivePatches', _patchHandler);
    if (_catchUpHandler) window.removeEventListener('hub.catchUpPatches', _catchUpHandler);

    // When the tree detects a seq gap it can't fill locally, request missing patches.
    const session = window['_DomSession'];
    if (session) {
        session.tree.off('change', _catchUpNeededHandler);
        _catchUpNeededHandler = ({ type, currentSeq }) => {
            if (type === 'catchUpNeeded' && _activePageId) {
                window.catchUp(_activePageId, currentSeq);
            }
        };
        session.tree.on('change', _catchUpNeededHandler);
    }

    _patchHandler = ({ detail: { pageId, patches } }) => {
        const session = window['_DomSession'];
        if (!session) return;
        if (pageId === _activePageId) {
            for (const patch of patches) {
                if (patch.type === 'move')
                    console.log('[hub] move confirmed — position:', patch);
                if (patch.result === 'success' || patch.result === 'noChange')
                    session.onReceivePatch(patch);
                else
                    session.onPatchRejected(patch, patch.result);
            }
        } else {
            // Different page changed — let the component system decide whether to recompile.
            window.dispatchEvent(new CustomEvent('project.pagePatched', { detail: { pageId, patches } }));
        }
    };

    _catchUpHandler = async ({ detail: { pageId, patches } }) => {
        if (pageId !== _activePageId) return;
        const session = window['_DomSession'];
        if (!session) return;

        if (patches === null) {
            // Patch log too old — snapshot is always current so just reload it.
            const page     = await window.getPage(projectId, pageId);
            if (!page) return;
            const nodes    = await window.getPageSnapshot(projectId, pageId);
            const snapshot = window.DomSession.snapshotFromServerNodes(page, nodes);
            const bridge   = window['_IframeBridge'];
            if (bridge) bridge.loadSnapshot(snapshot);
            else session.loadSnapshot(snapshot);
        } else {
            // CatchUpPatches sends PagePatch rows; the actual patch lives in patchJson.
            const treePatchs = patches.map(p => typeof p.patchJson === 'string' ? JSON.parse(p.patchJson) : p);
            session.applyRemote(treePatchs);
        }
    };

    window.addEventListener('hub.receivePatches', _patchHandler);
    window.addEventListener('hub.catchUpPatches', _catchUpHandler);
}
